import { describeError } from '@/lib/form/errorMessages';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  Permission,
  createDrawerRequest,
  type CatalogItemDetails,
  type CreateCatalogItemRequest,
  type CreateDrawerRequest,
  type DrawerSystemVariantListItem,
  type MaterialListItem,
  type PreviewDrawerRequest,
  type UpdateCatalogItemRequest,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { Form, TextField, TextareaField, FormActions, FormErrorBanner } from '@/components/form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import { useAuth } from '@/features/auth/useAuth';
import {
  useCatalogItem,
  useCreateCatalogItem,
  useDeleteCatalogItem,
  useUpdateCatalogItem,
} from '@/features/catalog/hooks';
import { errorKeyFor } from '@/features/catalog/errorMessages';
import { CatalogDangerZone } from '@/features/catalog/components/CatalogDangerZone';
import {
  EdgeAutosuggest,
  MaterialAutosuggest,
} from '@/features/project-items/shared/pickers/CatalogAutosuggest';
import { DrawerSystemSelector } from '@/features/project-items/shared/pickers/DrawerSystemSelector';
import { BreakdownPanel } from '@/features/pricing/components/BreakdownPanel';
import { usePreviewDrawer } from '@/features/pricing/hooks';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { edgesApi } from '@/features/catalog/components/admin/api';
import { chooseEdgeForMaterial } from '@/features/project-items/shared/edgeAutoSelect';
import { DrawerHboxField } from '@/features/project-items/shared/DrawerHboxField';
import { useDefaultMaterialId } from '@/features/project-items/shared/useDefaultMaterial';

const positiveInteger = z.string().regex(/^[1-9]\d*$/, 'Must be a positive integer');
const decimalString = z.string().regex(/^\d+(\.\d+)?$/, 'Expected decimal string');

const formSchema = z.object({
  name: z.string().trim().min(1, 'Required').max(200),
  sku: z.string().trim().max(50).optional(),
  heightMm: positiveInteger,
  widthMm: positiveInteger,
  depthMm: positiveInteger,
  materialId: z.string().min(1, 'Required'),
  frontMaterialId: z.string(),
  frontEdgeId: z.string(),
  carcassMaterial: z.string().optional(),
  frontMaterial: z.string().optional(),
  // Filled in from the chosen variant - see AddDrawerDialog for the rationale.
  modelSertar: z.string().min(1, 'Required'),
  tipSertar: z.string().optional(),
  descriere: z.string().optional(),
  detaliu: z.string().optional(),
  color: z.string().optional(),
  hBox: z.coerce.string().min(1, 'Required'),
  drawerSystemVariantId: z.string().min(1, 'Required'),
  unitPriceNet: decimalString,
  notes: z.string().trim().max(2000).optional(),
});
type FormValues = z.infer<typeof formSchema>;
type CatalogDrawerAttributes = Omit<CreateDrawerRequest, 'quantity' | 'coefficient'>;

function defaults(defaultMaterialId?: string): FormValues {
  return {
    name: '',
    sku: '',
    heightMm: '',
    widthMm: '',
    depthMm: '',
    materialId: defaultMaterialId ?? '',
    frontMaterialId: '',
    frontEdgeId: '',
    carcassMaterial: '',
    frontMaterial: '',
    modelSertar: '',
    tipSertar: '',
    descriere: '',
    detaliu: '',
    color: '',
    hBox: '',
    drawerSystemVariantId: '',
    unitPriceNet: '0.00',
    notes: '',
  };
}

/**
 * Box H-box codes are letters (N/M/K/C); Glisare box heights are plain
 * millimetres. A non-numeric value therefore came from the catalog and is shown
 * read-only rather than as an editable field.
 */
function initialCatalogHBox(hBox: string | undefined): string | null {
  if (!hBox) return null;
  return Number.isFinite(Number(hBox)) ? null : hBox;
}

function integerFormValue(value: string | number | null | undefined, fallback = ''): string {
  if (value == null || value === '') return fallback;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? String(Math.trunc(parsed)) : fallback;
}

function parseAttributes(item: CatalogItemDetails | undefined): Partial<CreateDrawerRequest> {
  if (!item?.attributes) return {};
  const parsed = createDrawerRequest.safeParse(item.attributes);
  return parsed.success ? parsed.data : {};
}

function valuesFromItem(item: CatalogItemDetails): FormValues {
  const attrs = parseAttributes(item);
  return {
    ...defaults(),
    name: attrs.name ?? item.name,
    sku: item.sku ?? '',
    heightMm: integerFormValue(attrs.heightMm ?? item.defaultHeightMm),
    widthMm: integerFormValue(attrs.widthMm ?? item.defaultWidthMm),
    depthMm: integerFormValue(attrs.depthMm ?? item.defaultDepthMm),
    materialId: attrs.materialId ?? '',
    frontMaterialId: attrs.frontMaterialId ?? '',
    frontEdgeId: attrs.frontEdgeId ?? attrs.edgeId ?? '',
    carcassMaterial: attrs.carcassMaterial ?? item.material ?? '',
    frontMaterial: attrs.frontMaterial ?? '',
    modelSertar: attrs.drawerModel ?? '',
    tipSertar: attrs.drawerType ?? '',
    descriere: attrs.slideModel ?? '',
    color: attrs.color ?? '',
    hBox: attrs.hBox ?? '',
    drawerSystemVariantId: attrs.drawerSystemVariantId ?? '',
    unitPriceNet: attrs.unitPriceNet ?? item.defaultCostNet ?? '0.00',
    notes: attrs.notes ?? item.description ?? '',
  };
}

function previewModelKind(modelSertar: string): PreviewDrawerRequest['model'] {
  if (modelSertar === 'Box sertar interior') return 'BOX_INTERIOR';
  if (modelSertar === 'Box') return 'BOX';
  if (modelSertar === 'Glisare sertar interior') return 'INTERIOR';
  return 'STANDARD';
}

function persistedModelKind(modelSertar: string): CreateDrawerRequest['drawerModelKind'] {
  if (modelSertar.startsWith('Box')) return 'BOX';
  if (modelSertar.includes('interior')) return 'INTERIOR';
  return 'STANDARD';
}

function buildDrawerBody(values: FormValues): CatalogDrawerAttributes {
  return {
    name: values.name,
    heightMm: parseInt(values.heightMm, 10),
    widthMm: parseInt(values.widthMm, 10),
    depthMm: parseInt(values.depthMm, 10),
    carcassMaterial: values.carcassMaterial || undefined,
    frontMaterial: values.frontMaterial || undefined,
    drawerModel: values.modelSertar,
    drawerType: values.tipSertar,
    slideModel: values.descriere,
    hBox: values.hBox,
    color: values.color || undefined,
    unitPriceNet: values.unitPriceNet,
    currency: 'RON',
    notes: values.notes || undefined,
    pricingMode: 'AUTO',
    materialId: values.materialId,
    edgeId: values.frontEdgeId || null,
    frontMaterialId: values.frontMaterialId || null,
    frontEdgeId: values.frontMaterialId ? values.frontEdgeId || null : null,
    drawerSystemVariantId: values.drawerSystemVariantId,
    hbox: null,
    drawerModelKind: persistedModelKind(values.modelSertar),
    cx2: false,
  };
}

function buildBody(
  values: FormValues,
  mode: 'create' | 'edit',
): CreateCatalogItemRequest | UpdateCatalogItemRequest {
  const drawer = buildDrawerBody(values);
  const body = {
    name: values.name,
    sku: values.sku || undefined,
    description: values.notes || undefined,
    defaultWidthMm: drawer.widthMm ?? null,
    defaultHeightMm: drawer.heightMm ?? null,
    defaultDepthMm: drawer.depthMm ?? null,
    material: drawer.carcassMaterial || undefined,
    color: drawer.color || undefined,
    defaultCostNet: drawer.unitPriceNet,
    currency: drawer.currency,
    attributes: drawer as Record<string, unknown>,
  };
  return mode === 'create' ? { type: 'DRAWER' as const, ...body } : body;
}

interface Props {
  mode: 'create' | 'edit';
}

export function CatalogDrawerFormPage({ mode }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };
  const id = mode === 'edit' ? params.id : undefined;
  const { user } = useAuth();
  const can = (p: string) => user?.permissions.includes(p) ?? false;
  const canUpdate = can(Permission.CATALOG_UPDATE);
  const canDelete = can(Permission.CATALOG_DELETE);
  const defaultMaterialId = useDefaultMaterialId();

  const itemQuery = useCatalogItem(id);
  const createMut = useCreateCatalogItem();
  const updateMut = useUpdateCatalogItem(id ?? '');
  const deleteMut = useDeleteCatalogItem(id ?? '');
  // Set when the chosen variant carries its own H-box (Box systems). Glisare
  // variants have none, so the user types the box height instead.
  const [catalogHBox, setCatalogHBox] = useState<string | null>(null);
  const [selectedCarcassMaterial, setSelectedCarcassMaterial] = useState<MaterialListItem | null>(
    null,
  );
  const [selectedFrontMaterial, setSelectedFrontMaterial] = useState<MaterialListItem | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults(defaultMaterialId),
  });

  useEffect(() => {
    if (mode !== 'edit' || !itemQuery.data) return;
    const next = valuesFromItem(itemQuery.data);
    setCatalogHBox(initialCatalogHBox(next.hBox));
    form.reset(next);
    setSelectedCarcassMaterial(null);
    setSelectedFrontMaterial(null);
  }, [form, itemQuery.data, mode]);

  // Fill default material once it resolves (async), only for new items.
  useEffect(() => {
    if (mode !== 'create' || !defaultMaterialId) return;
    const current = form.getValues('materialId');
    if (!current) {
      form.setValue('materialId', defaultMaterialId);
    }
  }, [defaultMaterialId, form, mode]);

  const watchedRaw = form.watch();
  const selectedEdgeMaterial = selectedFrontMaterial ?? selectedCarcassMaterial;
  const edgeSearch = selectedEdgeMaterial?.manufacturer?.trim() ?? '';
  const edgesQuery = useQuery({
    queryKey: ['catalog-edges', 'drawer-auto-select', edgeSearch],
    queryFn: () =>
      edgesApi
        .list({
          page: 1,
          pageSize: 100,
          active: true,
          includeDeleted: false,
          search: edgeSearch,
        })
        .then((res) => res.items),
    enabled:
      Boolean(selectedEdgeMaterial) &&
      selectedEdgeMaterial?.category !== 'MDF_VOPSIT' &&
      Boolean(edgeSearch),
    meta: { silent: true },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!selectedEdgeMaterial) return;
    if (selectedEdgeMaterial.category === 'MDF_VOPSIT' || !edgeSearch) {
      form.setValue('frontEdgeId', '', { shouldDirty: true, shouldValidate: true });
      form.clearErrors('frontEdgeId');
      return;
    }
    if (!edgesQuery.data) return;
    const edge = chooseEdgeForMaterial(selectedEdgeMaterial, edgesQuery.data);
    form.setValue('frontEdgeId', edge?.id ?? '', { shouldDirty: true, shouldValidate: true });
    form.clearErrors('frontEdgeId');
  }, [edgeSearch, edgesQuery.data, form, selectedEdgeMaterial]);

  const watched = useDebouncedValue(watchedRaw, 300);

  /**
   * Copy the chosen variant's own attributes onto the form, so what gets saved
   * always describes the product that was selected rather than the criteria
   * used to find it.
   */
  const applyVariant = (variant: DrawerSystemVariantListItem) => {
    const attrs = variant.attributes ?? {};
    const kind = attrs.sourceKind;
    const interior = attrs.interior === true;
    const model =
      kind === 'BOX'
        ? interior
          ? 'Box sertar interior'
          : 'Box'
        : kind === 'GLISARE'
          ? interior
            ? 'Glisare sertar interior'
            : 'Glisare'
          : '';
    const set = (fieldName: keyof FormValues, value: string) =>
      form.setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
    set('modelSertar', model);
    set('tipSertar', attrs.tipSertar ?? '');
    set('descriere', attrs.descriere ?? '');
    set('detaliu', attrs.detaliu ?? '');
    set('color', attrs.color ?? '');
    setCatalogHBox(attrs.hBox ?? null);
    if (attrs.hBox) set('hBox', attrs.hBox);
  };

  const previewBody: PreviewDrawerRequest | null = useMemo(() => {
    if (
      !watched.materialId ||
      !watched.drawerSystemVariantId ||
      !watched.hBox ||
      !watched.modelSertar ||
      !watched.heightMm ||
      !watched.widthMm ||
      !watched.depthMm
    ) {
      return null;
    }
    const heightMm = parseInt(watched.heightMm, 10);
    const widthMm = parseInt(watched.widthMm, 10);
    const depthMm = parseInt(watched.depthMm, 10);
    if (!heightMm || !widthMm || !depthMm) return null;
    return {
      hBox: watched.hBox as PreviewDrawerRequest['hBox'],
      heightMm,
      widthMm,
      depthMm,
      model: previewModelKind(watched.modelSertar),
      carcassMaterialId: watched.materialId,
      carcassEdgeId: watched.frontEdgeId || null,
      frontMaterialId: watched.frontMaterialId || null,
      frontEdgeId: watched.frontMaterialId ? watched.frontEdgeId || null : null,
      drawerSystemVariantId: watched.drawerSystemVariantId,
      quantity: '1',
      coefficient: '1',
    };
  }, [watched]);
  const preview = usePreviewDrawer(previewBody);

  useEffect(() => {
    if (preview.data) {
      const subtotal = parseFloat(preview.data.breakdown.subtotalNet);
      if (Number.isFinite(subtotal) && subtotal >= 0) {
        form.setValue('unitPriceNet', subtotal.toFixed(2), { shouldDirty: false });
      }
    }
  }, [form, preview.data]);

  const isReadOnly = mode === 'edit' && !canUpdate;
  const submitting = mode === 'create' ? createMut.isPending : updateMut.isPending;
  const submitError = mode === 'create' ? createMut.error : updateMut.error;
  const priceReady = Boolean(preview.data && !preview.isFetching && !preview.error);
  const selectedDrawerSystemLabel = form.getValues('drawerSystemVariantId')
    ? [
        watchedRaw.modelSertar,
        watchedRaw.tipSertar,
        watchedRaw.descriere,
        watchedRaw.hBox ? `H-box ${watchedRaw.hBox}` : null,
      ]
        .filter(Boolean)
        .join(' / ')
    : undefined;

  const onSubmit = async (values: FormValues) => {
    try {
      const body = buildBody(values, mode);
      if (mode === 'create') {
        await createMut.mutateAsync(body as CreateCatalogItemRequest);
        void navigate({ to: '/drawers', search: {} });
        return;
      }
      if (!id) return;
      await updateMut.mutateAsync(body as UpdateCatalogItemRequest);
    } catch {
      /* surfaced via mutation errors */
    }
  };

  const onDelete = async () => {
    if (!id) return;
    try {
      await deleteMut.mutateAsync();
      void navigate({ to: '/drawers', search: {} });
    } catch {
      /* surfaced via deleteMut.error */
    }
  };

  if (mode === 'edit' && itemQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }
  if (mode === 'edit' && itemQuery.isError) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        {describeError(t, itemQuery.error, errorKeyFor)}
      </div>
    );
  }

  const titleKey = mode === 'create' ? 'drawers.create.title' : 'drawers.edit.title';

  return (
    <div className="w-full space-y-4">
      <Link
        to="/drawers"
        search={{}}
        className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> {t('drawers.backToList')}
      </Link>
      <h1 className="text-2xl font-semibold text-foreground">{t(titleKey)}</h1>

      <Form
        form={form}
        onSubmit={onSubmit}
        className="space-y-4 rounded-md border border-border bg-card p-4"
      >
        <fieldset disabled={isReadOnly} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <TextField name="name" label={t('drawers.fields.name')} autoFocus />
            <TextField name="sku" label={t('catalog.fields.sku')} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <TextField
              name="heightMm"
              label={`${t('drawers.fields.heightMm')} *`}
              {...positiveIntegerFieldProps}
              required
            />
            <TextField
              name="widthMm"
              label={`${t('drawers.fields.widthMm')} *`}
              {...positiveIntegerFieldProps}
              required
            />
            <TextField
              name="depthMm"
              label={`${t('drawers.fields.depthMm')} *`}
              {...positiveIntegerFieldProps}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <MaterialAutosuggest
              name="materialId"
              label={t('drawers.fields.carcassMaterial')}
              usageScope="GENERAL"
              required
              onSelectedOption={(item) => {
                setSelectedCarcassMaterial(item);
                form.setValue('carcassMaterial', item?.name ?? '', { shouldDirty: true });
                if (!selectedFrontMaterial) {
                  form.setValue('frontEdgeId', '', {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
            />
            <MaterialAutosuggest
              name="frontMaterialId"
              label={t('drawers.fields.frontMaterial')}
              usageScope="GENERAL"
              emptyOptionLabel={t('pricing.noMaterial')}
              onSelectedOption={(item) => {
                setSelectedFrontMaterial(item);
                form.setValue('frontMaterial', item?.name ?? '', { shouldDirty: true });
                form.setValue('frontEdgeId', '', {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
            />
          </div>

          <div className="grid grid-cols-1 gap-3">
            <EdgeAutosuggest
              name="frontEdgeId"
              label="Cant Front"
              emptyOptionLabel={t('pricing.noEdge')}
            />
          </div>

          <DrawerSystemSelector
            name="drawerSystemVariantId"
            label="Selectare sistem"
            required
            selectedLabel={selectedDrawerSystemLabel}
            nearDepthMm={watchedRaw.depthMm ? parseInt(watchedRaw.depthMm, 10) : undefined}
            onVariantSelected={applyVariant}
          />

          <div className="grid grid-cols-3 gap-3">
            {catalogHBox ? (
              <div>
                <p className="text-sm font-medium">H-box</p>
                <p className="mt-1 rounded-md border border-border bg-muted px-3 py-2 text-sm">
                  {catalogHBox}
                </p>
              </div>
            ) : (
              <DrawerHboxField heightMm={watchedRaw.heightMm} />
            )}
          </div>

          <TextareaField name="notes" label={t('drawers.fields.notes')} rows={3} />

          <BreakdownPanel
            breakdown={preview.data?.breakdown ?? null}
            loading={preview.isFetching}
            error={preview.error}
          />
        </fieldset>

        <FormErrorBanner error={submitError} resolveKey={errorKeyFor} />

        {!isReadOnly && (
          <FormActions
            submitLabel={t(mode === 'create' ? 'drawers.create.submit' : 'common.save')}
            isSubmitting={submitting}
            submitDisabled={(!form.formState.isDirty && mode === 'edit') || !priceReady}
          >
            <Link to="/drawers" search={{}}>
              <Button type="button" variant="ghost">
                {t('common.cancel')}
              </Button>
            </Link>
          </FormActions>
        )}
      </Form>

      {mode === 'edit' && canDelete && itemQuery.data && (
        <CatalogDangerZone
          itemName={itemQuery.data.name}
          pending={deleteMut.isPending}
          error={deleteMut.error}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}
